import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import DirectionsIcon from '@mui/icons-material/Directions'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined'
import { HOME_MAP_DARK_MODE } from '../../../config'
import { getEnvVariables } from '../../../config/getEnvVars'
import { GridItemComponent } from '../styled'

const LOCATION_POINTS = [
  'Easy pull-in access off US 77',
  'Convenient for home, business, and vehicle storage',
  'Quick route for Schulenburg-area move-ins',
]

export const HomeBottomRowRightComponent: React.FC = () => {
  const { MAP_KEY, PLACE_ID } = getEnvVariables()
  const mapEmbedSrc =
    MAP_KEY && PLACE_ID
      ? `https://www.google.com/maps/embed/v1/place?q=place_id:${PLACE_ID}&key=${MAP_KEY}`
      : undefined

  return (
    <GridItemComponent
      sx={{
        alignItems: 'stretch',
        justifyContent: 'stretch',
        overflow: 'hidden',
        border: '1px solid rgba(173, 223, 255, 0.08)',
        background:
          'radial-gradient(circle at top right, rgba(78, 164, 208, 0.18) 0%, rgba(78, 164, 208, 0) 30%), linear-gradient(180deg, rgba(14, 18, 24, 0.96) 0%, rgba(11, 15, 20, 1) 100%)',
        boxShadow: '0 18px 36px rgba(0, 0, 0, 0.2)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Box
          sx={{
            px: { xs: 2.25, sm: 3 },
            py: 2.4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 0.8,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.8,
              maxWidth: 470,
            }}
          >
            <Typography
              sx={{
                color: 'rgba(180, 226, 255, 0.94)',
                fontSize: '0.74rem',
                fontWeight: 800,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
              }}
            >
              Location
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: '#f6fbff',
                fontSize: { xs: '1.45rem', sm: '1.7rem' },
                fontWeight: 700,
                lineHeight: 1.1,
              }}
            >
              Easy access right off US 77, with room to get in and out fast.
            </Typography>
            <Typography
              sx={{
                color: 'rgba(230, 240, 250, 0.8)',
                fontSize: '0.98rem',
                lineHeight: 1.55,
              }}
            >
              Whether you are stopping by after work or moving a trailer on the
              weekend, the property is simple to reach and easy to navigate.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            px: { xs: 2.25, sm: 3 },
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 1.8,
          }}
        >
          <Box
            sx={{
              flex: '1 1 230px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 1.1,
              px: 1.3,
              py: 1.15,
              border: '1px solid rgba(173, 223, 255, 0.12)',
              backgroundColor: 'rgba(15, 22, 31, 0.56)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.1,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  color: '#9edaff',
                  backgroundColor: 'rgba(37, 74, 103, 0.34)',
                  flexShrink: 0,
                }}
              >
                <PlaceOutlinedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: 'rgba(180, 226, 255, 0.82)',
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                  }}
                >
                  Address
                </Typography>
                <Typography
                  sx={{
                    color: '#f6fbff',
                    fontSize: '0.96rem',
                    fontWeight: 600,
                    lineHeight: 1.4,
                  }}
                >
                  2247 South US 77, Schulenburg, TX 78956
                </Typography>
              </Box>
            </Box>
            <Button
              component="a"
              href="https://goo.gl/maps/yEZN95uVfmM2"
              target="_blank"
              rel="noreferrer"
              variant="outlined"
              startIcon={<DirectionsIcon />}
              fullWidth
              sx={{
                mt: 'auto',
                px: 2.5,
                whiteSpace: 'nowrap',
                color: 'rgba(232, 244, 255, 0.92)',
                borderColor: 'rgba(173, 223, 255, 0.24)',
                backgroundColor: 'rgba(16, 23, 32, 0.4)',
                '&:hover': {
                  borderColor: 'rgba(173, 223, 255, 0.42)',
                  backgroundColor: 'rgba(16, 23, 32, 0.62)',
                },
              }}
            >
              Get Directions
            </Button>
          </Box>

          <Box
            sx={{
              flex: '1 1 230px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.1,
              px: 1.3,
              py: 1.15,
              border: '1px solid rgba(173, 223, 255, 0.12)',
              backgroundColor: 'rgba(15, 22, 31, 0.56)',
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                color: '#9edaff',
                backgroundColor: 'rgba(37, 74, 103, 0.34)',
                flexShrink: 0,
              }}
            >
              <RouteOutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
              <Typography
                sx={{
                  color: 'rgba(180, 226, 255, 0.82)',
                  fontSize: '0.7rem',
                  fontWeight: 800,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                }}
              >
                Nearby Benefits
              </Typography>
              {LOCATION_POINTS.map((point) => (
                <Typography
                  key={point}
                  sx={{
                    color: 'rgba(230, 240, 250, 0.76)',
                    fontSize: '0.9rem',
                    lineHeight: 1.4,
                  }}
                >
                  {point}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            px: { xs: 2.25, sm: 3 },
            pb: { xs: 2.25, sm: 3 },
            minHeight: 340,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              height: '100%',
              overflow: 'hidden',
              border: '1px solid rgba(173, 223, 255, 0.12)',
              boxShadow:
                '0 18px 40px rgba(3, 8, 18, 0.22), inset 0 1px 0 rgba(255,255,255,0.04)',
              minHeight: 340,
            }}
          >
            {mapEmbedSrc ? (
              <>
                <iframe
                  width="100%"
                  height="100%"
                  src={mapEmbedSrc}
                  style={{
                    border: 0,
                    filter: HOME_MAP_DARK_MODE
                      ? 'invert(90%) hue-rotate(180deg) saturate(0.85) brightness(0.92) contrast(0.96)'
                      : 'none',
                    minHeight: 340,
                  }}
                  loading="lazy"
                  allowFullScreen
                  title="map"
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    background:
                      'linear-gradient(180deg, rgba(10, 16, 22, 0.18) 0%, rgba(10, 16, 22, 0) 26%, rgba(10, 16, 22, 0) 74%, rgba(10, 16, 22, 0.2) 100%)',
                  }}
                />
              </>
            ) : (
              <Box
                sx={{
                  height: '100%',
                  minHeight: 340,
                  px: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  background:
                    'linear-gradient(180deg, rgba(11, 15, 20, 0.94) 0%, rgba(13, 18, 24, 0.98) 100%)',
                }}
              >
                <Typography
                  sx={{
                    color: 'rgba(230, 240, 250, 0.76)',
                    fontSize: '0.95rem',
                    lineHeight: 1.5,
                    maxWidth: 320,
                  }}
                >
                  Map preview is unavailable until the Infisical-backed
                  `VITE_MAP_KEY` and `VITE_PLACE_ID` values are present for this
                  build.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </GridItemComponent>
  )
}
